import { Theme, css, useTheme } from '@emotion/react';
import { useHover } from '@react-aria/interactions';
import { useAtom } from 'jotai';
import * as React from 'react';

import { foldAtom, openedAtom } from '~src/category/state/category-extesion-atoms';
import { ChevronDownIcon, ChevronUpIcon } from '~src/icons';
import { fontStyles } from '~src/theme/fontStyles';
import { useIpc } from '~src/utils/ipc-utils';

import { TextProperties } from './open-modal-button';

interface OpenListButtonLayoutProps {
  onClick: () => void;
  title?: TextProperties;
  description?: TextProperties;
  ariaLabel?: string;
  textSize?: 'small' | 'normal';
  innerRef: React.RefObject<HTMLDivElement>;
}

export const OpenListButtonLayout: React.FC<React.PropsWithChildren<OpenListButtonLayoutProps>> = ({
  onClick,
  innerRef,
  title,
  description,
  ariaLabel,
  textSize,
  children,
}) => {
  const { hoverProps, isHovered } = useHover({});
  const [opened] = useAtom(openedAtom);
  const [fold] = useAtom(foldAtom);
  const ipc = useIpc();
  const theme = useTheme();

  const didMount = React.useRef(false);
  React.useEffect(() => {
    if (didMount.current) {
      if (fold === 'min' && innerRef.current) {
        const rect = innerRef.current.getBoundingClientRect();
        ipc.send({ name: 'request_scroll_change', data: rect.top });
      }
    } else {
      didMount.current = true;
    }
  }, [fold]);

  return (
    <div
      ref={innerRef}
      css={styles.listWrapper(opened, isHovered)}
      className="focus-visible-outline"
      {...hoverProps}
    >
      <button css={styles.button} aria-label={ariaLabel} onClick={onClick}>
        <span css={styles.textWrapper}>
          {title && title.text && <span css={styles.textStyle(title, textSize)}>{title.text}</span>}
          {!opened && description && description.text && (
            <span css={styles.textStyle(description, textSize)}>{description.text}</span>
          )}
        </span>
        {opened ? <ChevronUpIcon color={theme.black} /> : <ChevronDownIcon color={theme.black} />}
      </button>
      {opened && children}
    </div>
  );
};

const styles = {
  listWrapper: (opened: boolean, hovered?: boolean) => (theme: Theme) => ({
    border: `1px solid ${hovered && !opened ? theme.neutral75 : theme.neutral85}`,
    borderRadius: '2px',
  }),

  button: (theme: Theme) =>
    css({
      textAlign: 'left',
      position: 'relative',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      minHeight: '60px',
      width: '100%',
      background: theme.white,
      border: 'none',
      borderRadius: '2px',
      padding: '8px 16px',
      cursor: 'pointer',
      ...fontStyles.px14_20,
    }),
  textWrapper: css({
    display: 'flex',
    flexDirection: 'column',
  }),
  textStyle: (text: TextProperties, textSize: OpenListButtonLayoutProps['textSize']) => ({
    color: text.color,
    fontVariationSettings: `"wght" ${text.textStyle === 'bold' ? 500 : 400}`,
    ...(textSize === 'small' ? fontStyles.px12_18 : fontStyles.px14_20),
  }),
};
